import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { calculateEstimatedDrivingDistanceInMiles } from "@src/appV2/Location/utils/geoDistance";
import { useCallback, useMemo, useState } from "react";

import { useWorkerLocations } from "../../GeoLocation/useWorkerLocations";
import { type Facility } from "../data/schemas";

interface UseEarlyAccessFacilitySelectionProps {
  facilities: Facility[];
}

export function useEarlyAccessFacilitySelection(props: UseEarlyAccessFacilitySelectionProps) {
  const { facilities } = props;
  const { workerLocation } = useWorkerLocations();
  const [selectedFacility, setSelectedFacility] = useState<Facility | undefined>(undefined);
  const modalState = useModalState();
  // Enrich facilities with distance if user location is available
  const facilitiesWithDistance = useMemo(() => {
    if (!isDefined(workerLocation)) {
      return facilities;
    }

    const facilitiesWithComputedDistance = facilities
      .map((facility) => ({
        ...facility,
        distance: calculateEstimatedDrivingDistanceInMiles(
          { latitude: workerLocation.lat, longitude: workerLocation.lng },
          { latitude: facility.lat, longitude: facility.lng }
        ),
      }))
      .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));

    return facilitiesWithComputedDistance;
  }, [workerLocation, facilities]);

  const handleFacilitySelect = useCallback(
    (facility: Facility) => {
      setSelectedFacility(facility);
      modalState.openModal();
      logEvent(APP_V2_USER_EVENTS.EARLY_ACCESS_FACILITY_SELECTED, {
        facilityId: facility.id,
        facilityName: facility.name,
      });
    },
    [modalState]
  );

  return {
    workerLocation,
    facilitiesWithDistance,
    selectedFacility,
    modalState,
    handleFacilitySelect,
  };
}
